/**
 * Journal Statistics Utility
 * Calculates statistics from trade log data and journal entries for the Journal page
 */

import { Trade } from "../types/trade";
import { JournalEntry } from "../types/journal";

export interface JournalStatistics {
  averagePreTradeEmotion: {
    mostCommon: string;
    percentage: number;
    distribution: { emotion: string; count: number; percentage: number }[];
  };
  planAdherenceRate: {
    rate: number;
    followed: number;
    deviated: number;
    total: number;
  };
  averageConfidence: {
    average: number;
    total: number;
    distribution: { range: string; count: number }[];
  };
  emotionalTradingFrequency: {
    percentage: number;
    emotional: number;
    disciplined: number;
    total: number;
  };
  bestPerformingEmotion: {
    emotion: string;
    averagePnL: number;
    totalTrades: number;
    worstEmotion?: string;
    worstAveragePnL?: number;
  };
  planAdherenceImpact: {
    followedPlanAvgPnL: number;
    deviatedAvgPnL: number;
    difference: number;
    followedCount: number;
    deviatedCount: number;
  };
  temptationFrequency: {
    percentage: number;
    tempted: number;
    total: number;
    earlyExit: number;
    addedPosition: number;
  };
  postTradeEmotionTrends: {
    mostCommon: string;
    percentage: number;
    distribution: { emotion: string; count: number; percentage: number }[];
  };
  confidenceVsPerformance: {
    correlation: "positive" | "negative" | "none";
    highConfidenceAvgPnL: number;
    lowConfidenceAvgPnL: number;
    message: string;
  };
  journalCompletionRate: {
    rate: number;
    complete: number;
    incomplete: number;
    total: number;
  };
}

/**
 * Calculate all journal statistics from trades and journal entries
 */
export const calculateJournalStatistics = (trades: Trade[], journalEntries: JournalEntry[] = []): JournalStatistics => {
  // Combine trades and journal entries for psychological tracking
  // For emotion/confidence/plan tracking, we can use both sources
  // For P&L calculations, we only use closed trades
  
  // Filter to closed trades for P&L calculations
  const closedTrades = trades.filter(t => t.status === "closed" && t.netPnL !== undefined);
  
  // Combine pre-trade emotions from trades and journal entries
  const allPreTradeEmotions = [
    ...trades.filter(t => t.preTradeEmotion).map(t => t.preTradeEmotion!),
    ...journalEntries.filter(e => e.preTradeEmotion).map(e => e.preTradeEmotion!),
  ];
  
  // 1. Average Pre-Trade Emotion (from trades and journal entries)
  const emotionCounts: { [key: string]: number } = {};
  allPreTradeEmotions.forEach(emotion => {
    emotionCounts[emotion] = (emotionCounts[emotion] || 0) + 1;
  });
  const emotionEntries = Object.entries(emotionCounts).map(([emotion, count]) => ({
    emotion,
    count,
    percentage: (allPreTradeEmotions.length > 0 ? (count / allPreTradeEmotions.length) * 100 : 0),
  }));
  const mostCommonEmotion = emotionEntries.length > 0
    ? emotionEntries.reduce((a, b) => a.count > b.count ? a : b).emotion
    : "N/A";
  const mostCommonPercentage = emotionEntries.length > 0
    ? emotionEntries.find(e => e.emotion === mostCommonEmotion)?.percentage || 0
    : 0;

  // 2. Plan Adherence Rate (from trades and journal entries)
  const planAdherenceData = [
    ...trades.filter(t => t.followedPlan !== undefined),
    ...journalEntries.filter(e => e.followedPlan !== undefined),
  ];
  const followedPlan = planAdherenceData.filter(d => d.followedPlan === true).length;
  const deviatedPlan = planAdherenceData.filter(d => d.followedPlan === false).length;
  const planAdherenceRate = planAdherenceData.length > 0
    ? (followedPlan / planAdherenceData.length) * 100
    : 0;

  // 3. Average Confidence Level (from trades and journal entries)
  const tradeConfidences = trades.filter(t => t.preTradeConfidence !== undefined).map(t => t.preTradeConfidence!);
  const journalConfidences = journalEntries.filter(e => e.preTradeConfidence !== undefined).map(e => e.preTradeConfidence!);
  const allConfidences = [...tradeConfidences, ...journalConfidences];
  const totalConfidence = allConfidences.reduce((sum, conf) => sum + conf, 0);
  const averageConfidence = allConfidences.length > 0
    ? totalConfidence / allConfidences.length
    : 0;
  
  // Confidence distribution
  const confidenceRanges = [
    { range: "1-3", min: 1, max: 3 },
    { range: "4-6", min: 4, max: 6 },
    { range: "7-8", min: 7, max: 8 },
    { range: "9-10", min: 9, max: 10 },
  ];
  const confidenceDistribution = confidenceRanges.map(range => ({
    range: range.range,
    count: allConfidences.filter(conf => conf >= range.min && conf <= range.max).length,
  }));

  // 4. Emotional Trading Frequency (from trades and journal entries)
  const tradeEmotional = trades.filter(t => t.emotionsInvolved !== undefined);
  const journalEmotional = journalEntries.filter(e => e.emotionsInvolved !== undefined);
  const emotionalData = [...tradeEmotional, ...journalEmotional];
  const emotionalCount = emotionalData.filter(d => {
    if ('emotionsInvolved' in d) {
      return (d as Trade).emotionsInvolved === true;
    } else {
      return (d as JournalEntry).emotionsInvolved === true;
    }
  }).length;
  const emotionalTradingPercentage = emotionalData.length > 0
    ? (emotionalCount / emotionalData.length) * 100
    : 0;

  // 5. Best Performing Emotion
  const emotionPnL: { [key: string]: { totalPnL: number; count: number } } = {};
  closedTrades.forEach(trade => {
    if (trade.preTradeEmotion && trade.netPnL !== undefined) {
      const emotion = trade.preTradeEmotion;
      if (!emotionPnL[emotion]) {
        emotionPnL[emotion] = { totalPnL: 0, count: 0 };
      }
      emotionPnL[emotion].totalPnL += trade.netPnL;
      emotionPnL[emotion].count += 1;
    }
  });
  
  const emotionAverages = Object.entries(emotionPnL).map(([emotion, data]) => ({
    emotion,
    averagePnL: data.count > 0 ? data.totalPnL / data.count : 0,
    totalTrades: data.count,
  }));
  
  const bestEmotion = emotionAverages.length > 0
    ? emotionAverages.reduce((a, b) => a.averagePnL > b.averagePnL ? a : b)
    : { emotion: "N/A", averagePnL: 0, totalTrades: 0 };
  
  const worstEmotion = emotionAverages.length > 0
    ? emotionAverages.reduce((a, b) => a.averagePnL < b.averagePnL ? a : b)
    : undefined;

  // 6. Plan Adherence Impact
  const followedPlanTrades = closedTrades.filter(t => t.followedPlan === true);
  const deviatedPlanTrades = closedTrades.filter(t => t.followedPlan === false);
  const followedPlanAvgPnL = followedPlanTrades.length > 0
    ? followedPlanTrades.reduce((sum, t) => sum + (t.netPnL || 0), 0) / followedPlanTrades.length
    : 0;
  const deviatedPlanAvgPnL = deviatedPlanTrades.length > 0
    ? deviatedPlanTrades.reduce((sum, t) => sum + (t.netPnL || 0), 0) / deviatedPlanTrades.length
    : 0;
  const planAdherenceDifference = followedPlanAvgPnL - deviatedPlanAvgPnL;

  // 7. Temptation Frequency (from trades and journal entries)
  const tradeTemptation = trades.filter(t => t.temptedEarlyExit !== undefined || t.addedToPosition !== undefined);
  const journalTemptation = journalEntries.filter(e => e.temptedEarlyExit !== undefined || e.addedToPosition !== undefined);
  const temptationData = [...tradeTemptation, ...journalTemptation];
  const temptedCount = temptationData.filter(d => {
    if ('temptedEarlyExit' in d) {
      const trade = d as Trade;
      return trade.temptedEarlyExit === true || trade.addedToPosition === true;
    } else {
      const entry = d as JournalEntry;
      return entry.temptedEarlyExit === true || entry.addedToPosition === true;
    }
  }).length;
  const earlyExitCount = [
    ...trades.filter(t => t.temptedEarlyExit === true),
    ...journalEntries.filter(e => e.temptedEarlyExit === true),
  ].length;
  const addedPositionCount = [
    ...trades.filter(t => t.addedToPosition === true),
    ...journalEntries.filter(e => e.addedToPosition === true),
  ].length;
  const temptationPercentage = temptationData.length > 0
    ? (temptedCount / temptationData.length) * 100
    : 0;

  // 8. Post-Trade Emotion Trends (from trades and journal entries)
  const allPostTradeEmotions = [
    ...trades.filter(t => t.postTradeEmotion).map(t => t.postTradeEmotion!),
    ...journalEntries.filter(e => e.postTradeEmotion).map(e => e.postTradeEmotion!),
  ];
  const postEmotionCounts: { [key: string]: number } = {};
  allPostTradeEmotions.forEach(emotion => {
    postEmotionCounts[emotion] = (postEmotionCounts[emotion] || 0) + 1;
  });
  const postEmotionEntries = Object.entries(postEmotionCounts).map(([emotion, count]) => ({
    emotion,
    count,
    percentage: (allPostTradeEmotions.length > 0 ? (count / allPostTradeEmotions.length) * 100 : 0),
  }));
  const mostCommonPostEmotion = postEmotionEntries.length > 0
    ? postEmotionEntries.reduce((a, b) => a.count > b.count ? a : b).emotion
    : "N/A";
  const mostCommonPostPercentage = postEmotionEntries.length > 0
    ? postEmotionEntries.find(e => e.emotion === mostCommonPostEmotion)?.percentage || 0
    : 0;

  // 9. Confidence vs Performance
  const highConfidenceTrades = closedTrades.filter(t => 
    t.preTradeConfidence !== undefined && (t.preTradeConfidence || 0) >= 7
  );
  const lowConfidenceTrades = closedTrades.filter(t => 
    t.preTradeConfidence !== undefined && (t.preTradeConfidence || 0) < 7
  );
  const highConfidenceAvgPnL = highConfidenceTrades.length > 0
    ? highConfidenceTrades.reduce((sum, t) => sum + (t.netPnL || 0), 0) / highConfidenceTrades.length
    : 0;
  const lowConfidenceAvgPnL = lowConfidenceTrades.length > 0
    ? lowConfidenceTrades.reduce((sum, t) => sum + (t.netPnL || 0), 0) / lowConfidenceTrades.length
    : 0;
  
  const correlation = highConfidenceAvgPnL > lowConfidenceAvgPnL ? "positive" :
    highConfidenceAvgPnL < lowConfidenceAvgPnL ? "negative" : "none";
  const correlationMessage = correlation === "positive"
    ? `Higher confidence = +$${Math.abs(highConfidenceAvgPnL - lowConfidenceAvgPnL).toFixed(0)} avg`
    : correlation === "negative"
    ? `Lower confidence = +$${Math.abs(lowConfidenceAvgPnL - highConfidenceAvgPnL).toFixed(0)} avg`
    : "No correlation";

  // 10. Journal Completion Rate
  // Check if trade or journal entry has at least 3 journaling fields filled
  const journalFields = [
    "preTradeRationale",
    "postTradeReview",
    "preTradeEmotion",
    "preTradeConfidence",
    "postTradeEmotion",
    "lessonsLearned",
    "content", // Journal entry content
    "moodRating", // Journal entry mood
  ];
  const allEntries = [...trades, ...journalEntries];
  const completeEntries = allEntries.filter(entry => {
    const filledFields = journalFields.filter(field => {
      const value = (entry as any)[field];
      return value !== undefined && value !== null && value !== "";
    });
    return filledFields.length >= 3;
  });
  const journalCompletionRate = allEntries.length > 0
    ? (completeEntries.length / allEntries.length) * 100
    : 0;

  return {
    averagePreTradeEmotion: {
      mostCommon: mostCommonEmotion,
      percentage: mostCommonPercentage,
      distribution: emotionEntries,
    },
    planAdherenceRate: {
      rate: planAdherenceRate,
      followed: followedPlan,
      deviated: deviatedPlan,
      total: planAdherenceData.length,
    },
    averageConfidence: {
      average: averageConfidence,
      total: allConfidences.length,
      distribution: confidenceDistribution,
    },
    emotionalTradingFrequency: {
      percentage: emotionalTradingPercentage,
      emotional: emotionalCount,
      disciplined: emotionalData.length - emotionalCount,
      total: emotionalData.length,
    },
    bestPerformingEmotion: {
      emotion: bestEmotion.emotion,
      averagePnL: bestEmotion.averagePnL,
      totalTrades: bestEmotion.totalTrades,
      worstEmotion: worstEmotion?.emotion,
      worstAveragePnL: worstEmotion?.averagePnL,
    },
    planAdherenceImpact: {
      followedPlanAvgPnL,
      deviatedAvgPnL: deviatedPlanAvgPnL,
      difference: planAdherenceDifference,
      followedCount: followedPlanTrades.length,
      deviatedCount: deviatedPlanTrades.length,
    },
    temptationFrequency: {
      percentage: temptationPercentage,
      tempted: temptedCount,
      total: temptationData.length,
      earlyExit: earlyExitCount,
      addedPosition: addedPositionCount,
    },
    postTradeEmotionTrends: {
      mostCommon: mostCommonPostEmotion,
      percentage: mostCommonPostPercentage,
      distribution: postEmotionEntries,
    },
    confidenceVsPerformance: {
      correlation,
      highConfidenceAvgPnL,
      lowConfidenceAvgPnL,
      message: correlationMessage,
    },
    journalCompletionRate: {
      rate: journalCompletionRate,
      complete: completeEntries.length,
      incomplete: allEntries.length - completeEntries.length,
      total: allEntries.length,
    },
  };
};

